import React, { useEffect, useMemo, useState } from "react";
import {
  Pencil,
  ShieldCheck,
  X,
  Activity,
  CheckCircle2,
  AlertCircle,
  Ban,
  Webhook,
  Database,
  KeyRound,
} from "lucide-react";
import Topbar from "@/components/layout/Topbar";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";

const AUTH_OPTIONS = [
  { value: "api_key", label: "API key" },
  { value: "bearer_token", label: "Bearer token" },
  { value: "basic_auth", label: "Basic auth (user+pass)" },
  { value: "oauth2_client_credentials", label: "OAuth2 (client credentials)" },
  { value: "hmac_signature", label: "HMAC signature (webhook)" },
  { value: "none", label: "No auth" },
];

const CRED_FIELDS = {
  api_key: [{ key: "api_key", label: "API key", type: "password" }],
  bearer_token: [{ key: "token", label: "Bearer token", type: "password" }],
  basic_auth: [
    { key: "username", label: "Username", type: "text" },
    { key: "password", label: "Password", type: "password" },
  ],
  oauth2_client_credentials: [
    { key: "client_id", label: "Client ID", type: "text" },
    { key: "client_secret", label: "Client secret", type: "password" },
    { key: "token_url", label: "Token URL", type: "text" },
    { key: "access_token", label: "Access token (pre-minted, optional)", type: "password" },
  ],
  hmac_signature: [{ key: "secret", label: "HMAC shared secret", type: "password" }],
  none: [],
};

const MODE_STYLES = {
  live: "bg-emerald-100 text-emerald-700",
  mock: "bg-slate-100 text-slate-600",
  disabled: "bg-red-100 text-red-700",
};

const MODE_ICON = {
  live: CheckCircle2,
  mock: Activity,
  disabled: Ban,
};

export default function ConnectorsAdmin() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/connectors/configs");
      setConfigs(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const ordered = useMemo(() => {
    const order = [
      "Chempax",
      "Syspro",
      "Infor",
      "Sage",
      "QuickBooks",
      "Misys",
      "Smartsheet",
    ];
    return [...configs].sort(
      (a, b) => order.indexOf(a.source_system) - order.indexOf(b.source_system)
    );
  }, [configs]);

  const openEdit = (c) => {
    setEditing({
      source_system: c.source_system,
      display_name: c.display_name,
      category: c.category,
      auth_type: c.auth_type,
      api_base: c.api_base || "",
      enabled: c.enabled,
      notes: c.notes || "",
      // empty credential values == "unchanged"; user explicitly clears via ""
      credentials: Object.fromEntries(
        (CRED_FIELDS[c.auth_type] || []).map((f) => [f.key, ""])
      ),
      credentials_filled: c.credentials_filled || {},
      credentials_masked: c.credentials_masked || {},
    });
  };

  const save = async () => {
    if (!editing) return;
    try {
      // Drop empty credential keys → backend treats missing as "unchanged"
      const creds = {};
      Object.entries(editing.credentials || {}).forEach(([k, v]) => {
        if (v !== "" && v != null) creds[k] = v;
      });

      const body = {
        auth_type: editing.auth_type,
        api_base: editing.api_base || null,
        enabled: editing.enabled,
        notes: editing.notes,
      };
      if (Object.keys(creds).length) body.credentials = creds;

      await api.patch(`/connectors/configs/${editing.source_system}`, body);
      toast.success(`${editing.source_system} saved`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const test = async (source_system) => {
    setTesting(source_system);
    try {
      const { data } = await api.post(
        `/connectors/configs/${source_system}/test`
      );
      if (data.ok) toast.success(`${source_system}: ${data.message}`);
      else toast.error(`${source_system}: ${data.message}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(null);
    }
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      <Topbar
        title="Connectors"
        subtitle="Configure ERP and webhook integrations for all 7 source systems"
      />

      <div className="p-6 animate-fade-in" data-testid="connectors-admin">
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {ordered.map((c) => {
              const ModeIcon = MODE_ICON[c.mode] || Activity;
              const CatIcon = c.category === "webhook" ? Webhook : Database;
              return (
                <div
                  key={c.source_system}
                  data-testid={`config-${c.source_system.toLowerCase()}`}
                  className="flex flex-col rounded-md border border-slate-200 bg-white shadow-sm"
                >
                  <div className="flex items-start justify-between border-b border-slate-100 p-5">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-navy-600 text-white">
                        <CatIcon className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="font-display text-lg font-semibold text-navy-600">
                          {c.display_name}
                        </div>
                        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                          {c.category} · {c.source_system}
                        </div>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
                        MODE_STYLES[c.mode] || "bg-slate-100"
                      }`}
                    >
                      <ModeIcon className="h-3 w-3" />
                      {c.mode}
                    </span>
                  </div>

                  <div className="flex-1 space-y-3 p-5 text-sm">
                    <Row
                      label="Auth"
                      value={
                        AUTH_OPTIONS.find((a) => a.value === c.auth_type)?.label ||
                        c.auth_type
                      }
                    />
                    <Row
                      label="Base URL"
                      value={c.api_base || "—"}
                      mono
                      truncate
                    />
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                        Credentials
                      </div>
                      <div className="mt-2 space-y-1">
                        {(CRED_FIELDS[c.auth_type] || []).length === 0 && (
                          <span className="text-xs text-slate-400">None required</span>
                        )}
                        {(CRED_FIELDS[c.auth_type] || []).map((f) => {
                          const filled = c.credentials_filled?.[f.key];
                          const masked = c.credentials_masked?.[f.key] || "";
                          return (
                            <div
                              key={f.key}
                              className="flex items-center justify-between gap-2 text-xs"
                            >
                              <div className="flex items-center gap-1.5 text-slate-600">
                                <KeyRound className="h-3 w-3 text-slate-400" />
                                {f.label}
                              </div>
                              <span
                                className={`mono tabular ${
                                  filled ? "text-navy-600" : "text-slate-400"
                                }`}
                              >
                                {filled ? masked || "set" : "—"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {c.notes && (
                      <div className="rounded-sm bg-slate-50 p-2 text-xs italic text-slate-600">
                        {c.notes}
                      </div>
                    )}
                    {c.updated_by && (
                      <div className="text-[10px] text-slate-400">
                        Last updated by {c.updated_by} ·{" "}
                        {c.updated_at
                          ? new Date(c.updated_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "—"}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 border-t border-slate-100 p-3">
                    <button
                      type="button"
                      onClick={() => openEdit(c)}
                      data-testid={`edit-${c.source_system.toLowerCase()}`}
                      className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-sm bg-brand-orange text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#C1701C]"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Configure
                    </button>
                    <button
                      type="button"
                      onClick={() => test(c.source_system)}
                      disabled={testing === c.source_system}
                      data-testid={`test-${c.source_system.toLowerCase()}`}
                      className="flex h-9 items-center justify-center gap-1.5 rounded-sm border border-slate-200 px-3 text-xs font-semibold uppercase tracking-widest text-slate-600 hover:border-navy-600 hover:text-navy-600 disabled:opacity-50"
                    >
                      <ShieldCheck className="h-3.5 w-3.5" />
                      {testing === c.source_system ? "…" : "Test"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-navy-600/70 p-4 backdrop-blur-sm"
          data-testid="connector-modal"
        >
          <div className="w-full max-w-2xl rounded-md border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-brand-orange">
                  {editing.category === "webhook" ? "Webhook" : "ERP"} connector
                </div>
                <div className="font-display text-lg font-semibold text-navy-600">
                  {editing.display_name}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-sm p-1 text-slate-400 hover:text-navy-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-[70vh] space-y-5 overflow-y-auto p-5">
              {/* Enabled toggle */}
              <label className="flex cursor-pointer items-center justify-between rounded-sm border border-slate-200 bg-slate-50 p-3">
                <div>
                  <div className="text-sm font-medium text-navy-600">Enabled</div>
                  <div className="text-xs text-slate-500">
                    Disable to skip this connector in ETL runs without deleting credentials.
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={editing.enabled}
                  onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
                  data-testid="connector-enabled"
                  className="h-5 w-5 accent-brand-orange"
                />
              </label>

              {/* Auth type */}
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  Auth type
                </label>
                <select
                  value={editing.auth_type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setEditing({
                      ...editing,
                      auth_type: newType,
                      credentials: Object.fromEntries(
                        (CRED_FIELDS[newType] || []).map((f) => [f.key, ""])
                      ),
                    });
                  }}
                  data-testid="connector-auth-type"
                  className="mt-1.5 block h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm outline-none focus:border-navy-600"
                >
                  {AUTH_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Base URL — hidden for HMAC / none */}
              {!["hmac_signature", "none"].includes(editing.auth_type) && (
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Base URL
                  </label>
                  <input
                    value={editing.api_base}
                    onChange={(e) => setEditing({ ...editing, api_base: e.target.value })}
                    data-testid="connector-api-base"
                    placeholder="https://api.example.com"
                    className="mt-1.5 block h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm outline-none focus:border-navy-600"
                  />
                </div>
              )}

              {/* Credential fields */}
              <div className="space-y-3 rounded-sm border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-brand-orange" />
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Credentials
                  </div>
                </div>
                {(CRED_FIELDS[editing.auth_type] || []).length === 0 && (
                  <div className="text-xs text-slate-500">No credentials required.</div>
                )}
                {(CRED_FIELDS[editing.auth_type] || []).map((f) => {
                  const filled = editing.credentials_filled?.[f.key];
                  const masked = editing.credentials_masked?.[f.key];
                  return (
                    <div key={f.key}>
                      <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                        {f.label}{" "}
                        {filled && (
                          <span className="ml-1 text-brand-orange">
                            (currently: {masked || "set"})
                          </span>
                        )}
                      </label>
                      <input
                        type={f.type}
                        value={editing.credentials?.[f.key] ?? ""}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            credentials: {
                              ...editing.credentials,
                              [f.key]: e.target.value,
                            },
                          })
                        }
                        data-testid={`connector-cred-${f.key}`}
                        placeholder={filled ? "Leave blank to keep current value" : "Enter value"}
                        className="mt-1.5 block h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm outline-none focus:border-navy-600"
                      />
                    </div>
                  );
                })}
              </div>

              {/* Notes */}
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  Notes
                </label>
                <textarea
                  value={editing.notes || ""}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  data-testid="connector-notes"
                  placeholder="Internal notes (e.g. rotation schedule, responsible team)"
                  rows={2}
                  className="mt-1.5 block w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-navy-600"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="h-9 rounded-sm border border-slate-200 px-3 text-xs font-semibold uppercase tracking-widest text-slate-600 hover:border-navy-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                data-testid="connector-save-button"
                className="h-9 rounded-sm bg-brand-orange px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#C1701C]"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Row({ label, value, mono, truncate }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
        {label}
      </span>
      <span
        className={[
          "text-xs",
          mono ? "mono" : "",
          truncate ? "max-w-[55%] truncate" : "",
          value === "—" ? "text-slate-400" : "text-navy-600",
        ].join(" ")}
        title={typeof value === "string" ? value : undefined}
      >
        {value}
      </span>
    </div>
  );
}
